
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const EmailVerificationHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const handleEmailVerification = async () => {
      // Check for verification tokens from email links
      const token = searchParams.get('token');
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      console.log('EmailVerification - URL params:', {
        token: token ? 'present' : 'none',
        tokenHash: tokenHash ? 'present' : 'none',
        type,
        error,
        errorDescription
      });

      // Handle errors first
      if (error) {
        console.error('Verification error:', error, errorDescription);
        if (error === 'access_denied') {
          toast.error('Email verification failed. The link may have expired.');
        } else {
          toast.error('Authentication error occurred. Please try again.');
        }
        navigate('/auth', { replace: true });
        return;
      }

      // Handle successful verification tokens (from Supabase emails)
      if (token && tokenHash && type === 'signup') {
        setIsVerifying(true);
        try {
          console.log('Processing email verification token...');
          console.log('Token hash present:', !!tokenHash);
          console.log('Raw token present:', !!token);
          
          // Use token_hash verification as primary method
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'signup'
          });
          
          if (error) {
            console.error('Verification error details:', {
              message: error.message,
              status: error.status,
              name: error.name
            });
            
            if (error.message?.includes('expired') || error.message?.includes('not found')) {
              toast.error('Verification link has expired. Please sign up again to receive a new verification email.');
            } else if (error.message?.includes('invalid')) {
              toast.error('Verification link is invalid. Please sign up again to receive a new verification email.');
            } else {
              toast.error('Email verification failed. Please try signing up again.');
            }
            navigate('/auth', { replace: true });
          } else if (data?.user) {
            console.log('Email verified successfully for user:', data.user.email);
            toast.success('Email verified! Welcome to Bins & Things!');
            
            // Redirect to home page after successful verification
            navigate('/', { replace: true });
          } else {
            console.error('No user data returned from verification');
            toast.error('Verification completed but user data is missing. Please try signing in.');
            navigate('/auth', { replace: true });
          }
        } catch (err) {
          console.error('Exception during verification:', err);
          toast.error('An error occurred during verification. Please try signing up again.');
          navigate('/auth', { replace: true });
        } finally {
          setIsVerifying(false);
        }
        return;
      }

      // Handle password recovery tokens
      if (token && tokenHash && type === 'recovery') {
        setIsVerifying(true);
        try {
          console.log('Processing password recovery token...');
          
          // Verify the recovery token with Supabase
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery'
          });

          if (verifyError) {
            console.error('Recovery verification error:', verifyError);
            toast.error('Password reset link is invalid or has expired.');
            navigate('/auth', { replace: true });
          } else if (data.user) {
            console.log('Recovery token verified successfully');
            toast.success('Password reset link verified! Please set your new password.');
            // The user will be automatically signed in, so we can show the password reset form
          }
        } catch (err) {
          console.error('Exception during recovery verification:', err);
          toast.error('An error occurred during password reset verification.');
          navigate('/auth', { replace: true });
        } finally {
          setIsVerifying(false);
        }
        return;
      }

      // Handle legacy verified=true parameter for backward compatibility
      const verifiedParam = searchParams.get('verified');
      if (verifiedParam === 'true' && !token) {
        console.log('Legacy verification parameter detected');
        toast.success('Email verified! You are now signed in.');
        navigate('/', { replace: true });
        return;
      }

      // If we have tokens but no type, or if verification URL looks malformed
      if ((token || tokenHash) && !type) {
        console.warn('Verification URL appears malformed - missing type parameter');
        toast.error('Verification link appears to be invalid. Please try signing up again.');
        navigate('/auth', { replace: true });
        return;
      }

      // If no verification params are present, just stay on current page
      if (!token && !tokenHash && !verifiedParam) {
        console.log('No verification parameters detected - normal page load');
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  if (isVerifying) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your email...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we confirm your account</p>
        </div>
      </div>
    );
  }

  // If no verification is happening, show a helpful message
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Email Verification</h2>
        <p className="text-gray-600 mb-4">Processing your verification link...</p>
        <p className="text-sm text-gray-500">
          If you're not redirected automatically, please try clicking the verification link again 
          or contact support if the problem persists.
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationHandler;
